import { createFileRoute } from '@tanstack/react-router'
import { useListGateLog, useListSwitches, useMe } from '@api/generated'
import type { GateLogEntry, SwitchLogEntry } from '@api/generated/models'
import { DataTable, type Features } from '@components/DataTable'
import { PageSpinner } from '@components/PageSpinner'
import { errorMessage } from '@utils/errors'
import type { ColumnDef } from '@tanstack/react-table'

export const Route = createFileRoute('/log')({ component: Log })

function status(e: SwitchLogEntry) {
  if (!e.ok) return `fehlgeschlagen${e.error ? `: ${e.error}` : ''}`
  return e.verified ? 'ok' : 'gesendet, nicht bestätigt'
}

const columns: ColumnDef<Features, SwitchLogEntry>[] = [
  {
    accessorKey: 'at',
    header: 'Zeitpunkt',
    cell: (info) => new Date(info.row.original.at).toLocaleString('de-DE'),
  },
  { accessorKey: 'source', header: 'Quelle' },
  { id: 'note', header: 'Grund', accessorFn: (e) => e.note ?? '' },
  {
    id: 'change',
    header: 'Änderung',
    accessorFn: (e) => `${e.fromPlate ?? '–'} → ${e.toPlate}`,
  },
  { id: 'status', header: 'Ergebnis', accessorFn: status },
]

const gateColumns: ColumnDef<Features, GateLogEntry>[] = [
  {
    accessorKey: 'at',
    header: 'Zeitpunkt',
    cell: (info) => new Date(info.row.original.at).toLocaleString('de-DE'),
  },
  { accessorKey: 'userName', header: 'Benutzer' },
  {
    id: 'status',
    header: 'Ergebnis',
    accessorFn: (e) =>
      e.ok ? 'geöffnet' : `fehlgeschlagen${e.error ? `: ${e.error}` : ''}`,
  },
]

function Log() {
  const me = useMe()
  const log = useListSwitches(
    { limit: 500 },
    { query: { enabled: !!me.data?.admin } },
  )
  const gateLog = useListGateLog(
    { limit: 500 },
    { query: { enabled: !!me.data?.admin } },
  )
  if (me.data && !me.data.admin) {
    return (
      <div role="alert" className="alert alert-error mx-4 sm:mx-8">
        Nur für Administratoren.
      </div>
    )
  }
  return (
    <div className="px-4 sm:px-8 space-y-4 sm:space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold">Umschaltprotokoll</h1>
      {log.isPending && <PageSpinner inline />}
      {log.isError && (
        <div role="alert" className="alert alert-error">
          {errorMessage(log.error)}
        </div>
      )}
      {log.data && (
        <DataTable
          data={log.data}
          columns={columns}
          emptyMessage="Noch keine Kennzeichenwechsel"
        />
      )}
      <h1 className="text-2xl sm:text-3xl font-bold">Torprotokoll</h1>
      {gateLog.isPending && <PageSpinner inline />}
      {gateLog.isError && (
        <div role="alert" className="alert alert-error">
          {errorMessage(gateLog.error)}
        </div>
      )}
      {gateLog.data && (
        <DataTable
          data={gateLog.data}
          columns={gateColumns}
          emptyMessage="Das Tor wurde noch nicht geöffnet"
        />
      )}
    </div>
  )
}
